import { displayDate } from "src/utils/dateHandler.util";
import { getLangNameByLangCode } from "src/utils/util";
import { ActiveSlotsReport } from "../types/slot.entity";
import { Link } from "react-router-dom";

type Props = {
  data: (ActiveSlotsReport & { id: string })[];
};

const statusBadgeClass = (status: ActiveSlotsReport["status"]) => {
  switch (status) {
    case "AVAILABLE":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "BOOKED":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "EXPIRED":
      return "bg-gray-100 text-gray-700 border-gray-200";
    case "SINGLE_REQUEST_PENDING":
      return "bg-amber-100 text-amber-800 border-amber-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export const ActiveSlotsReportsTable: React.FC<Props> = ({ data }) => {
  return (
    <div className="overflow-x-auto mb-6">
      <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
        <thead className="bg-gray-900 text-white text-center">
          <tr>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Slot</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">User</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Lang</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Status</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Created</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">When</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Reported At</th>
          </tr>
        </thead>

        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-4 text-xl text-gray-500">
                No Active Slots Reports
              </td>
            </tr>
          )}

          {data.map((report) => {
            const scheduledAt = report.date instanceof Date ? report.date : (report.date as any).toDate?.() ?? undefined;
            const createdAt = report.createdAt instanceof Date ? report.createdAt : (report.createdAt as any).toDate?.() ?? undefined;
            const reportedAt = report.reportedAt instanceof Date ? report.reportedAt : (report.reportedAt as any).toDate?.() ?? undefined;
            return (
              <tr key={report.id} className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                <td className="px-4 py-3 border-t border-gray-200">
                  <Link
                    to={`/slots/${report.id}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                    title={report.id}
                  >
                    {report.id}
                  </Link>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <Link
                    to={`/users/${report.userId}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                  >
                    {report.userId}
                  </Link>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span className="font-medium">{getLangNameByLangCode(report.langCode)}</span>
                    <span className="text-xs text-gray-500">{report.langCode}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <span className={`inline-block px-2 py-1 rounded-full border text-xs font-semibold ${statusBadgeClass(
                    report.status
                  )}`}>
                    {report.status.replaceAll("_", " ")}
                  </span>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span>{createdAt ? createdAt.toLocaleDateString() : displayDate(report.createdAt)}</span>
                    <span className="text-xs text-gray-500">{createdAt ? createdAt.toLocaleTimeString() : ""}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span>{scheduledAt ? scheduledAt.toLocaleDateString() : displayDate(report.date)}</span>
                    <span className="text-xs text-gray-500">{scheduledAt ? scheduledAt.toLocaleTimeString() : ""}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span>{reportedAt ? reportedAt.toLocaleDateString() : displayDate(report.reportedAt)}</span>
                    <span className="text-xs text-gray-500">{reportedAt ? reportedAt.toLocaleTimeString() : ""}</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ActiveSlotsReportsTable;
